import transactionsOnboardingCard1 from 'assets/images/transactionsOnboardingCard1.png';
import transactionsOnboardingCard2 from 'assets/images/transactionsOnboardingCard2.png';
import transactionsOnboardingCard3 from 'assets/images/transactionsOnboardingCard3.png';
import type FreeTextInputProps from 'components/atoms/Form/FreeTextInput/FreeTextInput.types';
import List from 'components/atoms/List';
import { Modal } from 'components/atoms/Modal';
import OnboardingCardsCarousel from 'components/molecules/OnboardingCardsCarousel/OnboardingCardsCarousel.component';
import { isValidIBAN } from 'ibantools';
import Joi from 'joi';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { BankField } from 'types/banks.types';
import { BankFieldType } from 'types/banks.types';
import type { Connector } from 'types/transactions.types';

export type BankLogoType = { name: string; img: string; bankCode?: string };

export const reasons = [
  {
    title: 'transactions.onboarding.card2.title',
    img: transactionsOnboardingCard2,
    description: 'transactions.onboarding.card2.sub_title',
    btnText: 'continue',
  },
  {
    title: 'transactions.onboarding.card3.title',
    img: transactionsOnboardingCard3,
    description: 'transactions.onboarding.card3.sub_title',
    btnText: 'continue',
  },
  {
    title: 'transactions.onboarding.card1.title',
    img: transactionsOnboardingCard1,
    Description: ({ country }: { country: string }) => (
      <List
        listClassName="mt-3 mb-12 text-init"
        itemClassName="!p-3 !px-7"
        data={[
          { content: `transactions.onboarding.card1.sub_title1.${country}` },
          { content: `transactions.onboarding.card1.sub_title2` },
          { content: `transactions.onboarding.card1.sub_title3` },
          { content: `transactions.onboarding.card1.sub_title4` },
        ]}
      />
    ),

    btnText: 'transactions.onboarding.card3.primary_button',
  },
];

export const ReasonsToConnectModal: FC = () => {
  const { t } = useTranslation();

  return (
    <Modal
      name="reasons to connect bank account"
      width="2xl"
      trigger={(api) => (
        <div>
          <p className="text-primary-800 text-center text-lg mt-6">
            {t('bank.onboarding.bottom_text')}
          </p>

          <button
            type="button"
            onClick={api.openModal}
            className="text-center w-full text-lg text-[#5AB7F9] hover:opacity-80 cursor-pointer font-bold underline"
          >
            {t('bank.onboarding.bottom_link')}
          </button>
        </div>
      )}
      render={(api) => (
        <OnboardingCardsCarousel cards={reasons} onLast={api.closeModal} />
      )}
    />
  );
};

export const IBANvalidationSchema = Joi.string()
  .required()
  .custom((value: string, helpers) => {
    if (!isValidIBAN(value.replace(/ /g, ''))) {
      return helpers.error('any.invalid');
    }
    return value;
  })
  .messages({ '*': 'settings.IBAN.any.invalid' });

export const emailValidationSchema = Joi.string()
  .required()
  .email({ tlds: { allow: false } })
  .messages({ '*': 'validation.string.email' });

type GetFormData = {
  email: string;
  fields?: BankField[];
  IBAN?: string;
  financialInstitutionSlug?: string;
};

type GetFormDataReturn = {
  validationSchema: Joi.ObjectSchema<any>;
  defaultValues: { [key: string]: any };
  field: {
    name: string;
    type: FreeTextInputProps['type'];
    bankFieldType: BankFieldType;
  };
};

const fieldKey = 'accountReference';

export const getFormData = ({
  email,
  fields,
  IBAN,
  financialInstitutionSlug,
}: GetFormData): GetFormDataReturn => {
  const customerReferenceSchema =
    financialInstitutionSlug === 'deutsche-bank'
      ? Joi.string()
          .required()
          .messages({ '*': 'settings.username.any.invalid' })
      : Joi.string().optional();

  const defaultIBANdata: GetFormDataReturn = {
    validationSchema: Joi.object({
      [fieldKey]: IBANvalidationSchema,
      customerReference: customerReferenceSchema,
    }),
    defaultValues: { accountCurrency: 'EUR' },
    field: {
      name: fieldKey,
      type: 'text',
      bankFieldType: BankFieldType.iban,
    },
  };

  const defaultEmaildata: GetFormDataReturn = {
    validationSchema: Joi.object({
      [fieldKey]: emailValidationSchema,
      customerReference: customerReferenceSchema,
    }),
    defaultValues: { accountCurrency: 'EUR' },
    field: {
      name: fieldKey,
      type: 'email',
      bankFieldType: BankFieldType.email,
    },
  };

  if (!fields) return defaultIBANdata;

  const [field] = fields.filter((_field) => _field.type);

  if (field.type === BankFieldType.iban) {
    defaultIBANdata.field.name = field.name || defaultIBANdata.field.name;
    defaultIBANdata.defaultValues[defaultIBANdata.field.name] = IBAN;

    return defaultIBANdata;
  }

  defaultEmaildata.field.name = field.name || defaultEmaildata.field.name;
  defaultEmaildata.defaultValues[defaultEmaildata.field.name] = email;
  return defaultEmaildata;
};

export const checkMatchingConnector = (
  connectors: Connector[],
  accountReference: string,
  accountCurrency: string,
): boolean =>
  !!(
    accountReference &&
    accountCurrency &&
    connectors?.length &&
    connectors?.some((connector) => {
      const bankRef = connector.bankAccountReference;

      const normalizedIBAN = bankRef?.IBAN?.replace(/\s/g, '');

      const normalizedReferenceIBAN = accountReference?.replace(/\s/g, '');

      return (
        (normalizedIBAN === normalizedReferenceIBAN ||
          bankRef?.email === accountReference) &&
        connector.balance?.currency === accountCurrency
      );
    })
  );
